import { useState, useMemo } from 'react'
import { format, parseISO, addDays } from 'date-fns'
import { Check, ChevronDown, ChevronLeft, ChevronRight, EyeOff, Eye, SlidersHorizontal } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useCalendarAllocation } from '@/hooks/useAllocation'
import { useBuckets } from '@/hooks/useBuckets'
import { useAllActivePriorities } from '@/hooks/usePriorities'
import { getBucketIcon } from '@/lib/bucketIcons'
import { cadenceActiveInWeek, priorityHoursPerWeek } from '@/lib/allocation'
import { useSkippedPriorityIds, useSkipPriorityWeek, useUnskipPriorityWeek } from '@/hooks/useTimeLogs'
import { toISODate, weekStartISO } from '@/lib/dates'
import { cn } from '@/lib/utils'
import type { AllocationResult, BucketAlloc, WeekSlice } from '@/lib/allocation'
import type { Bucket, Priority } from '@/types'

type CalView = 'year' | 'month' | 'week'

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_FULL  = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const BW_AMBER = 55
const BW_RED   = 65

function bwStatus(hrs: number): AllocationResult['bandwidthStatus'] {
  if (hrs >= BW_RED)   return 'red'
  if (hrs >= BW_AMBER) return 'amber'
  return 'ok'
}

// FR-C5 — alignment overlay helpers
// "Critical" buckets: top 3 active non-system buckets with a target allocation set.
function criticalBuckets(bucketMap: Map<string, Bucket>): Bucket[] {
  return [...bucketMap.values()]
    .filter(b => !b.isSystem && !b.archivedAt && b.targetAlloc != null)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, 3)
}

// Returns the first critical bucket that is significantly under-allocated in this week.
// "Significant" = actual share < 50% of target.
function misalignedFlagBucket(week: WeekSlice, criticals: Bucket[]): Bucket | null {
  return criticals.find(b => {
    const actual = week.slices.find(s => s.bucketId === b.id)?.share ?? 0
    return actual < b.targetAlloc! * 0.5
  }) ?? null
}

// ─── Root view ────────────────────────────────────────────────────────────────

export function CalendarView() {
  const [year, setYear]       = useState(new Date().getFullYear())
  const [view, setView]       = useState<CalView>('year')
  const [month, setMonth]     = useState(new Date().getMonth())
  const [weekStart, setWeekStart] = useState(() => weekStartISO())
  const [filterBucketId, setFilterBucketId] = useState<string | null>(null)
  const [filterPriorityId, setFilterPriorityId] = useState<string | null>(null)
  const [focusSheetOpen, setFocusSheetOpen] = useState(false)

  // Expand query range to cover selected week when in week view
  const from = useMemo(() => {
    if (view === 'week') {
      const d = parseISO(weekStart)
      return new Date(d.getFullYear(), 0, 1)
    }
    return new Date(year, 0, 1)
  }, [year, view, weekStart])

  const to = useMemo(() => {
    if (view === 'week') {
      const d = parseISO(weekStart)
      return new Date(d.getFullYear(), 11, 31)
    }
    return new Date(year, 11, 31)
  }, [year, view, weekStart])

  const allocation  = useCalendarAllocation(from, to)
  const buckets     = useBuckets()
  const priorities  = useAllActivePriorities()

  const bucketMap = useMemo(
    () => new Map((buckets ?? []).map((b) => [b.id, b])),
    [buckets],
  )

  const focusPriority = useMemo(
    () => (filterPriorityId ? (priorities ?? []).find((p) => p.id === filterPriorityId) : null),
    [filterPriorityId, priorities],
  )

  const filteredWeekSlices = useMemo(() => {
    if (!allocation) return []

    // Individual priority focus: synthetic slices from cadence
    if (focusPriority) {
      const bucket = focusPriority.bucketId ? bucketMap.get(focusPriority.bucketId) : null
      const color = focusPriority.color ?? bucket?.color ?? 'hsl(var(--muted-foreground))'
      const h = priorityHoursPerWeek(focusPriority)
      return allocation.weekSlices.map((w, idx) => {
        const ws = parseISO(w.weekStart)
        const active = h > 0 && !!focusPriority.cadence && cadenceActiveInWeek(focusPriority, ws, idx)
        return {
          ...w,
          slices: active ? [{ bucketId: focusPriority.bucketId ?? '', color, share: 1, weight: h }] : [],
          totalWeight: active ? h : 0,
        }
      })
    }

    if (!filterBucketId) return allocation.weekSlices
    return allocation.weekSlices.map((w) => ({
      ...w,
      slices: w.slices.filter((s) => s.bucketId === filterBucketId),
    }))
  }, [allocation, filterBucketId, focusPriority, bucketMap])

  const monthSlices = useMemo(() => {
    return filteredWeekSlices.filter((w) => {
      const d = parseISO(w.weekStart)
      return d.getMonth() === month || addDays(d, 6).getMonth() === month
    })
  }, [filteredWeekSlices, month])

  const selectedWeekSlice = useMemo(() => {
    if (!allocation || view !== 'week') return null
    return allocation.weekSlices.find((w) => w.weekStart === weekStart) ?? null
  }, [allocation, weekStart, view])

  function prevWeek() {
    const d = parseISO(weekStart)
    const prev = toISODate(addDays(d, -7))
    setWeekStart(prev)
    setYear(parseISO(prev).getFullYear())
  }
  function nextWeek() {
    const d = parseISO(weekStart)
    const next = toISODate(addDays(d, 7))
    setWeekStart(next)
    setYear(parseISO(next).getFullYear())
  }
  function goToday() {
    const mon = weekStartISO()
    setWeekStart(mon)
    setYear(parseISO(mon).getFullYear())
  }

  const noData = allocation && allocation.totalWeight === 0
  const weekSunday = weekStart ? addDays(parseISO(weekStart), 6) : null

  const periodLabel =
    view === 'year'  ? String(year)
    : view === 'month' ? `${MONTH_FULL[month]} ${year}`
    : weekStart && weekSunday
      ? `${format(parseISO(weekStart), 'MMM d')} – ${format(weekSunday, 'MMM d, yyyy')}`
      : ''

  return (
    <div className="flex flex-col md:flex-row md:h-view min-h-0">
      {/* ── Left: calendar grid ─────────────────────────────── */}
      <div className="flex-1 min-w-0 space-y-5 md:overflow-y-auto md:pr-8">

        {/* Controls row */}
        <div className="flex items-center justify-between gap-x-4 gap-y-2 flex-wrap">
          {/* Year nav (hidden in week view — week nav handles it) */}
          {view !== 'week' ? (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-9 w-9 md:h-7 md:w-7" onClick={() => setYear((y) => y - 1)} aria-label="Previous year">
                <ChevronLeft size={14} />
              </Button>
              <span className="font-semibold text-base w-12 text-center tabular-nums">{year}</span>
              <Button variant="ghost" size="icon" className="h-9 w-9 md:h-7 md:w-7" onClick={() => setYear((y) => y + 1)} aria-label="Next year">
                <ChevronRight size={14} />
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-9 w-9 md:h-7 md:w-7" onClick={prevWeek} aria-label="Previous week">
                <ChevronLeft size={14} />
              </Button>
              <span className="text-sm font-medium tabular-nums whitespace-nowrap">{periodLabel}</span>
              <Button variant="ghost" size="icon" className="h-9 w-9 md:h-7 md:w-7" onClick={nextWeek} aria-label="Next week">
                <ChevronRight size={14} />
              </Button>
              {weekStart !== weekStartISO() && (
                <button
                  type="button"
                  onClick={goToday}
                  className="ml-1 text-[11px] px-2.5 py-1.5 md:px-2 md:py-0.5 rounded border border-border text-muted-foreground hover:text-foreground transition-colors"
                >
                  Today
                </button>
              )}
            </div>
          )}

          {/* View switcher */}
          <div className="flex items-center gap-0.5 text-xs">
            {(['week', 'month', 'year'] as CalView[]).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setView(v)}
                className={cn(
                  'px-3 py-2.5 md:py-1.5 rounded transition-colors capitalize focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  view === v
                    ? 'bg-secondary font-medium text-foreground'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {v === 'year' ? 'Year' : v === 'month' ? 'Month' : 'Week'}
              </button>
            ))}
          </div>
        </div>

        {/* Mobile: focus filter behind a single action */}
        {buckets && buckets.filter((b) => !b.archivedAt).length > 0 && (
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setFocusSheetOpen(true)}
              className={cn(
                'flex items-center gap-2 px-3.5 py-2.5 rounded-lg border text-sm transition-colors max-w-full',
                filterBucketId || filterPriorityId
                  ? 'border-ring bg-secondary text-foreground font-medium'
                  : 'border-border text-muted-foreground',
              )}
              aria-label="Choose focus filter"
            >
              <SlidersHorizontal size={14} className="flex-shrink-0" />
              <span className="truncate">
                {focusPriority
                  ? focusPriority.title
                  : filterBucketId
                    ? bucketMap.get(filterBucketId)?.name ?? 'Focus'
                    : 'Focus: all areas'}
              </span>
              <ChevronDown size={14} className="flex-shrink-0 opacity-60" />
            </button>
            <FocusSheet
              open={focusSheetOpen}
              onOpenChange={setFocusSheetOpen}
              buckets={buckets.filter((b) => !b.archivedAt)}
              priorities={priorities ?? []}
              filterBucketId={filterBucketId}
              filterPriorityId={filterPriorityId}
              onSelectBucket={(id) => { setFilterBucketId(id); setFilterPriorityId(null) }}
              onSelectPriority={(id) => { setFilterPriorityId(id); setFocusSheetOpen(false) }}
              onClear={() => { setFilterBucketId(null); setFilterPriorityId(null); setFocusSheetOpen(false) }}
            />
          </div>
        )}

        {/* Desktop: bucket + priority filter chips */}
        {buckets && buckets.filter((b) => !b.archivedAt).length > 0 && (
          <div className="space-y-1.5 hidden md:block">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] text-muted-foreground flex-shrink-0">Focus:</span>
              {buckets.filter((b) => !b.archivedAt).map((b) => {
                const ChipIcon = getBucketIcon(b.icon)
                const isActive = filterBucketId === b.id
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => {
                      if (isActive) { setFilterBucketId(null); setFilterPriorityId(null) }
                      else { setFilterBucketId(b.id); setFilterPriorityId(null) }
                    }}
                    className={cn(
                      'flex items-center gap-1 px-2.5 py-1.5 md:px-2 md:py-0.5 rounded-full text-[11px] border transition-colors',
                      isActive
                        ? 'border-foreground bg-foreground text-background font-medium'
                        : 'border-border text-muted-foreground hover:text-foreground hover:border-ring',
                    )}
                  >
                    {ChipIcon
                      ? <ChipIcon size={10} style={{ color: isActive ? 'currentColor' : b.color }} aria-hidden />
                      : <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />}
                    <span className="truncate max-w-[72px]">{b.name}</span>
                  </button>
                )
              })}
              {(filterBucketId || filterPriorityId) && (
                <button
                  type="button"
                  onClick={() => { setFilterBucketId(null); setFilterPriorityId(null) }}
                  className="text-[11px] text-muted-foreground hover:text-foreground transition-colors underline-offset-2 underline"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Priority chips — shown when a bucket is selected */}
            {filterBucketId && (() => {
              const bucketPriorities = (priorities ?? []).filter(
                (p) => p.bucketId === filterBucketId && p.cadence && p.status !== 'dormant',
              )
              if (!bucketPriorities.length) return null
              const bucket = bucketMap.get(filterBucketId)
              return (
                <div className="flex items-center gap-1.5 flex-wrap pl-4 md:pl-[3.25rem]">
                  {bucketPriorities.map((p) => {
                    const isActive = filterPriorityId === p.id
                    const color = p.color ?? bucket?.color ?? 'hsl(var(--muted-foreground))'
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setFilterPriorityId(isActive ? null : p.id)}
                        className={cn(
                          'flex items-center gap-1 px-2.5 py-1.5 md:px-2 md:py-0.5 rounded-full text-[11px] border transition-colors',
                          isActive
                            ? 'border-foreground bg-foreground text-background font-medium'
                            : 'border-border text-muted-foreground hover:text-foreground hover:border-ring',
                        )}
                      >
                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: isActive ? 'currentColor' : color }} />
                        <span className="truncate max-w-[96px]">{p.title}</span>
                      </button>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        )}

        {/* No-data notice */}
        {noData && view !== 'week' && (
          <p className="text-sm text-muted-foreground italic">
            No allocation data yet. Add a cadence to a priority to see it here.
          </p>
        )}

        {/* Grid */}
        <TooltipProvider delayDuration={120}>
          {view === 'year' && allocation && (
            <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 md:overflow-x-visible">
              <div className="min-w-max">
                <YearGrid
                  weekSlices={filteredWeekSlices}
                  bucketMap={bucketMap}
                  year={year}
                  cadencedPriorities={(priorities ?? []).filter(
                    (p) => p.cadence && p.status !== 'dormant'
                      && (!filterBucketId || p.bucketId === filterBucketId)
                      && (!filterPriorityId || p.id === filterPriorityId),
                  )}
                />
              </div>
            </div>
          )}
          {view === 'month' && allocation && (
            <MonthGrid
              weekSlices={monthSlices}
              month={month}
              year={year}
              bucketMap={bucketMap}
              onMonthChange={setMonth}
            />
          )}
          {view === 'week' && (
            <WeekGrid
              weekStart={weekStart}
              weekSlice={selectedWeekSlice}
              bucketMap={bucketMap}
              priorities={(priorities ?? []).filter(
                (p) => (!filterBucketId || p.bucketId === filterBucketId)
                  && (!filterPriorityId || p.id === filterPriorityId),
              )}
            />
          )}
        </TooltipProvider>

        {/* Bandwidth bar */}
        {allocation && (
          <BandwidthBar
            committed={
              view === 'week' && selectedWeekSlice
                ? selectedWeekSlice.totalWeight
                : allocation.committedHoursPerWeek
            }
            status={
              view === 'week' && selectedWeekSlice
                ? bwStatus(selectedWeekSlice.totalWeight)
                : allocation.bandwidthStatus
            }
            label={view === 'week' ? 'This week' : 'Steady-state'}
          />
        )}
      </div>

      {/* ── Right: allocation sidebar — pinned on desktop, stacked below on mobile ── */}
      <div className="w-full md:w-64 flex-shrink-0 border-t md:border-t-0 md:border-l border-border/50 md:pl-6 pt-6 mt-8 md:pt-0 md:mt-0 md:overflow-y-auto pb-4 md:pb-0">
        {allocation && buckets ? (
          <AllocationPanel
            byBucket={(() => {
              const base = view === 'week' && selectedWeekSlice
                ? selectedWeekSlice.slices.map((s) => ({
                    bucketId: s.bucketId,
                    share: s.share,
                    targetAlloc: bucketMap.get(s.bucketId)?.targetAlloc ?? null,
                    gap: bucketMap.get(s.bucketId)?.targetAlloc != null
                      ? (bucketMap.get(s.bucketId)!.targetAlloc! - s.share)
                      : null,
                    weight: selectedWeekSlice.totalWeight * s.share,
                  }))
                : allocation.byBucket
              return filterBucketId ? base.filter((b) => b.bucketId === filterBucketId) : base
            })()}
            bucketMap={bucketMap}
            alignmentScore={allocation.alignmentScore}
            totalWeight={view === 'week' && selectedWeekSlice ? selectedWeekSlice.totalWeight : allocation.totalWeight}
            period={periodLabel}
          />
        ) : (
          <div className="h-32 flex items-center justify-center text-xs text-muted-foreground">Loading…</div>
        )}
      </div>
    </div>
  )
}

// ─── Mobile focus picker sheet ────────────────────────────────────────────────
// Tapping an area filters to it and reveals its cadenced priorities nested
// beneath, so the area→priority association stays visible while narrowing.

function FocusSheet({
  open,
  onOpenChange,
  buckets,
  priorities,
  filterBucketId,
  filterPriorityId,
  onSelectBucket,
  onSelectPriority,
  onClear,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  buckets: Bucket[]
  priorities: Priority[]
  filterBucketId: string | null
  filterPriorityId: string | null
  onSelectBucket: (id: string) => void
  onSelectPriority: (id: string) => void
  onClear: () => void
}) {
  const allSelected = !filterBucketId && !filterPriorityId

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange} title="Focus">
      <div className="px-5 pb-8 space-y-1">
        <button
          type="button"
          onClick={onClear}
          className={cn(
            'w-full flex items-center justify-between px-3 py-3 rounded-lg text-sm text-left transition-colors',
            allSelected ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-muted/40',
          )}
        >
          All areas
          {allSelected && <Check size={16} className="flex-shrink-0" />}
        </button>

        {buckets.map((b) => {
          const isArea = filterBucketId === b.id
          const areaSelected = isArea && !filterPriorityId
          const nested = isArea
            ? priorities.filter((p) => p.bucketId === b.id && p.cadence && p.status !== 'dormant')
            : []
          return (
            <div key={b.id}>
              <button
                type="button"
                onClick={() => onSelectBucket(b.id)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-3 rounded-lg text-sm text-left transition-colors',
                  areaSelected ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-muted/40',
                  isArea && !areaSelected && 'text-foreground',
                )}
                aria-expanded={isArea}
              >
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: b.color }} />
                <span className="flex-1 truncate">{b.name}</span>
                {areaSelected && <Check size={16} className="flex-shrink-0" />}
              </button>

              {/* Nested cadenced priorities of the focused area */}
              {isArea && nested.length > 0 && (
                <div className="ml-5 pl-3 border-l border-border/60 space-y-0.5 py-1">
                  {nested.map((p) => {
                    const selected = filterPriorityId === p.id
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => onSelectPriority(p.id)}
                        className={cn(
                          'w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-left transition-colors',
                          selected ? 'bg-secondary font-medium' : 'text-muted-foreground hover:bg-muted/40',
                        )}
                      >
                        <span
                          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: p.color ?? b.color }}
                        />
                        <span className="flex-1 truncate">{p.title}</span>
                        {selected && <Check size={15} className="flex-shrink-0" />}
                      </button>
                    )
                  })}
                </div>
              )}
              {isArea && nested.length === 0 && (
                <p className="ml-8 py-1.5 text-xs text-muted-foreground italic">
                  No cadenced priorities in this area.
                </p>
              )}
            </div>
          )
        })}

        <div className="pt-3">
          <Button className="w-full h-11" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </div>
    </BottomSheet>
  )
}

// ─── Year grid (52 stacked week bars) ────────────────────────────────────────

function YearGrid({
  weekSlices,
  bucketMap,
  year,
  cadencedPriorities,
}: {
  weekSlices: WeekSlice[]
  bucketMap: Map<string, Bucket>
  year: number
  cadencedPriorities: Priority[]
}) {
  const criticals = criticalBuckets(bucketMap)

  return (
    <div className="space-y-1">
      {/* Month labels */}
      <div className="flex gap-[2px]">
        {weekSlices.map((week, idx) => {
          const d = parseISO(week.weekStart)
          const prev = idx > 0 ? parseISO(weekSlices[idx - 1]!.weekStart) : null
          const isNewMonth = !prev || prev.getMonth() !== d.getMonth()
          const showLabel = isNewMonth && d.getFullYear() === year
          return (
            <div key={week.weekStart} className="w-[14px] flex-shrink-0 relative">
              {showLabel && (
                <span className="absolute left-0 text-[9px] text-muted-foreground whitespace-nowrap leading-none">
                  {MONTH_SHORT[d.getMonth()]}
                </span>
              )}
            </div>
          )
        })}
      </div>

      {/* Week bars — clickable to open skip popover */}
      <div className="flex gap-[2px] pt-3">
        {weekSlices.map((week) => (
          <YearWeekBar
            key={week.weekStart}
            week={week}
            bucketMap={bucketMap}
            cadencedPriorities={cadencedPriorities}
          />
        ))}
      </div>

      {/* Bandwidth dots — one per week, amber/red when over threshold */}
      <div className="flex gap-[2px] mt-[3px]" aria-hidden>
        {weekSlices.map((week) => (
          <div
            key={week.weekStart}
            className={cn(
              'w-[14px] h-[4px] flex-shrink-0 rounded-sm',
              week.totalWeight >= BW_RED   ? 'bg-destructive/70'
              : week.totalWeight >= BW_AMBER ? 'bg-amber-500/70'
              : 'bg-transparent',
            )}
            title={week.totalWeight >= BW_AMBER ? `${Math.round(week.totalWeight)} est. hrs` : undefined}
          />
        ))}
      </div>

      {/* FR-C5 — Alignment flag dots — one per week, bucket color when a top-priority bucket is under-allocated */}
      {criticals.length > 0 && (
        <div className="flex gap-[2px] mt-[2px]">
          {weekSlices.map((week) => {
            const flagBucket = misalignedFlagBucket(week, criticals)
            return (
              <div
                key={week.weekStart}
                className="w-[14px] h-[4px] flex-shrink-0 rounded-sm"
                style={flagBucket ? { backgroundColor: flagBucket.color, opacity: 0.75 } : undefined}
                title={flagBucket
                  ? `${flagBucket.name}: target ${Math.round(flagBucket.targetAlloc! * 100)}%, under-allocated`
                  : undefined}
              >
                {flagBucket && (
                  <span className="sr-only">
                    {flagBucket.name} under-allocated week of {week.weekStart}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

function YearWeekBar({
  week,
  bucketMap,
  cadencedPriorities,
}: {
  week: WeekSlice
  bucketMap: Map<string, Bucket>
  cadencedPriorities: Priority[]
}) {
  const monday = parseISO(week.weekStart)
  const sunday = addDays(monday, 6)
  const dateRange = `${format(monday, 'MMM d')} – ${format(sunday, 'MMM d')}`
  const sliceDesc = week.slices.length > 0
    ? week.slices.map(s => `${bucketMap.get(s.bucketId)?.name ?? 'Unknown'} ${Math.round(s.share * 100)}%`).join(', ')
    : null
  const ariaLabel = week.totalWeight > 0
    ? `Week of ${week.weekStart} · ${Math.round(week.totalWeight)} est. hrs${sliceDesc ? ` · ${sliceDesc}` : ''}`
    : `Week of ${week.weekStart} · no data`

  const bar = (
    <div
      className="rounded-sm overflow-hidden flex flex-col flex-shrink-0 cursor-pointer hover:ring-1 hover:ring-ring/60 transition-shadow"
      style={{ height: 72, width: 14 }}
      role="button"
      aria-label={ariaLabel}
    >
      {week.totalWeight === 0 ? (
        <div className="h-full bg-muted/25" />
      ) : (
        week.slices.map((slice) => (
          <div
            key={slice.bucketId}
            style={{ height: `${slice.share * 100}%`, backgroundColor: slice.color }}
          />
        ))
      )}
    </div>
  )

  return (
    <Popover>
      <PopoverTrigger asChild>{bar}</PopoverTrigger>
      <PopoverContent side="bottom" align="start" className="w-64 p-0">
        <WeekSkipContent
          weekStart={week.weekStart}
          dateRange={dateRange}
          cadencedPriorities={cadencedPriorities}
          bucketMap={bucketMap}
        />
      </PopoverContent>
    </Popover>
  )
}

// ─── Month grid (bigger bars, one per week) ───────────────────────────────────

function MonthGrid({
  weekSlices,
  month,
  year,
  bucketMap,
  onMonthChange,
}: {
  weekSlices: WeekSlice[]
  month: number
  year: number
  bucketMap: Map<string, Bucket>
  onMonthChange: (m: number) => void
}) {
  const criticals = criticalBuckets(bucketMap)

  return (
    <div className="space-y-4">
      {/* Month nav */}
      <div className="flex items-center gap-1">
        <Button
          variant="ghost" size="icon" className="h-9 w-9 md:h-7 md:w-7"
          onClick={() => onMonthChange((month + 11) % 12)}
          aria-label="Previous month"
        >
          <ChevronLeft size={14} />
        </Button>
        <span className="text-sm font-medium w-32 text-center">
          {MONTH_FULL[month]} {year}
        </span>
        <Button
          variant="ghost" size="icon" className="h-9 w-9 md:h-7 md:w-7"
          onClick={() => onMonthChange((month + 1) % 12)}
          aria-label="Next month"
        >
          <ChevronRight size={14} />
        </Button>
      </div>

      {/* Week rows */}
      <div className="space-y-2">
        {weekSlices.length === 0 && (
          <p className="text-xs text-muted-foreground italic">No data for this month.</p>
        )}
        {weekSlices.map((week) => {
          const flagBucket = criticals.length > 0 ? misalignedFlagBucket(week, criticals) : null
          return (
            <div key={week.weekStart} className="flex items-center gap-3">
              <span className="text-[11px] text-muted-foreground w-14 flex-shrink-0 tabular-nums">
                {format(parseISO(week.weekStart), 'MMM d')}
              </span>
              <div className="flex-1">
                <WeekBar week={week} height={36} bucketMap={bucketMap} />
              </div>
              {/* FR-C5 — alignment flag */}
              {flagBucket && (
                <span
                  className="w-2 h-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: flagBucket.color }}
                  role="status"
                  aria-label={`${flagBucket.name} under-allocated: target ${Math.round(flagBucket.targetAlloc! * 100)}%`}
                  title={`${flagBucket.name}: target ${Math.round(flagBucket.targetAlloc! * 100)}%, under-allocated`}
                />
              )}
              {week.totalWeight >= BW_AMBER && (
                <span
                  className={cn(
                    'text-[10px] tabular-nums flex-shrink-0 w-10 text-right',
                    week.totalWeight >= BW_RED ? 'text-destructive' : 'text-amber-600',
                  )}
                >
                  {Math.round(week.totalWeight)}h
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ─── Week grid (single-week detail) ──────────────────────────────────────────

const CADENCE_LABEL: Record<string, string> = {
  daily: 'daily',
  weekly: 'weekly',
  biweekly: 'every 2 wks',
  monthly: 'monthly',
  quarterly: 'quarterly',
}

function WeekGrid({
  weekStart,
  weekSlice,
  bucketMap,
  priorities,
}: {
  weekStart: string
  weekSlice: WeekSlice | null
  bucketMap: Map<string, Bucket>
  priorities: Priority[]
}) {
  const sunday = addDays(parseISO(weekStart), 6)
  const weekOf = `${format(parseISO(weekStart), 'MMM d')} – ${format(sunday, 'MMM d')}`

  const skippedIds = useSkippedPriorityIds(weekStart)
  const skipPriorityWeek = useSkipPriorityWeek()
  const unskipPriorityWeek = useUnskipPriorityWeek()

  const weekPriorities = useMemo(() => {
    const active = priorities.filter(
      (p) => p.status !== 'dormant' && (p.cadence || (p.hoursPerWeek ?? 0) > 0),
    )
    return active.sort((a, b) => {
      const ra = a.bucketId ? (bucketMap.get(a.bucketId)?.rank ?? 999) : 999
      const rb = b.bucketId ? (bucketMap.get(b.bucketId)?.rank ?? 999) : 999
      if (ra !== rb) return ra - rb
      return a.rank - b.rank
    })
  }, [priorities, bucketMap])

  const groups = useMemo(() => {
    const map = new Map<string, Priority[]>()
    for (const p of weekPriorities) {
      const key = p.bucketId ?? '__none__'
      if (!map.has(key)) map.set(key, [])
      map.get(key)!.push(p)
    }
    return [...map.entries()]
  }, [weekPriorities])

  return (
    <div className="space-y-5">
      {/* Big week allocation bar */}
      <div className="space-y-2">
        <p className="text-[11px] text-muted-foreground">{weekOf}</p>
        {weekSlice ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="rounded-md overflow-hidden flex flex-col w-full cursor-default"
                style={{ height: 64 }}
                role="img"
                aria-label={`Week of ${weekStart} · ${Math.round(weekSlice.totalWeight)} est. hrs${weekSlice.slices.length > 0 ? ' · ' + weekSlice.slices.map(s => `${bucketMap.get(s.bucketId)?.name ?? 'Unknown'} ${Math.round(s.share * 100)}%`).join(', ') : ''}`}
              >
                {weekSlice.totalWeight === 0 ? (
                  <div className="h-full bg-muted/25 flex items-center justify-center">
                    <span className="text-xs text-muted-foreground italic">No cadence data for this week</span>
                  </div>
                ) : (
                  weekSlice.slices.map((slice) => (
                    <div
                      key={slice.bucketId}
                      style={{ height: `${slice.share * 100}%`, backgroundColor: slice.color }}
                    />
                  ))
                )}
              </div>
            </TooltipTrigger>
            {weekSlice.totalWeight > 0 && (
              <TooltipContent side="top" className="p-0 overflow-hidden min-w-[140px]">
                <WeekTooltipContent week={weekSlice} bucketMap={bucketMap} dateRange={weekOf} />
              </TooltipContent>
            )}
          </Tooltip>
        ) : (
          <div
            className="rounded-md bg-muted/25 flex items-center justify-center"
            style={{ height: 64 }}
          >
            <span className="text-xs text-muted-foreground italic">No allocation data for this week</span>
          </div>
        )}
        {weekSlice && weekSlice.totalWeight > 0 && (
          <p className={cn(
            'text-[11px]',
            weekSlice.totalWeight >= BW_RED   ? 'text-destructive'
            : weekSlice.totalWeight >= BW_AMBER ? 'text-amber-600'
            : 'text-muted-foreground',
          )}>
            ~{Math.round(weekSlice.totalWeight)} est. hrs committed
          </p>
        )}
      </div>

      {/* Priority list */}
      <div className="space-y-3">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
          This week's priorities
        </p>
        {groups.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            No cadenced priorities yet. Set a cadence on a priority to see it here.
          </p>
        ) : (
          groups.map(([bucketId, items]) => {
            const bucket = bucketId !== '__none__' ? bucketMap.get(bucketId) : null
            return (
              <div key={bucketId} className="space-y-1">
                {bucket && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: bucket.color }} />
                    <span className="text-[11px] font-medium text-muted-foreground truncate">{bucket.name}</span>
                  </div>
                )}
                {items.map((p) => {
                  const color = p.color ?? bucket?.color ?? 'hsl(var(--muted-foreground))'
                  const hrsLabel = p.hoursPerWeek
                    ? `${p.hoursPerWeek}h/wk`
                    : p.loadWeight
                    ? `~${p.loadWeight * 4}h/wk`
                    : null
                  const skipped = skippedIds?.has(p.id) ?? false
                  return (
                    <div key={p.id} className="flex items-start gap-2 pl-3.5">
                      <div
                        className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                        style={{ backgroundColor: color }}
                      />
                      <div className="flex-1 min-w-0">
                        <span className={cn('text-xs leading-snug', skipped ? 'line-through text-muted-foreground' : 'text-foreground')}>
                          {p.title}
                        </span>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {p.cadence && (
                            <span className="text-[10px] text-muted-foreground">
                              {CADENCE_LABEL[p.cadence] ?? p.cadence}
                            </span>
                          )}
                          {hrsLabel && !skipped && (
                            <span className="text-[10px] text-muted-foreground/60">{hrsLabel}</span>
                          )}
                          {skipped && (
                            <span className="text-[10px] text-amber-600">skipped</span>
                          )}
                          {p.status === 'aspirational' && !skipped && (
                            <span className="text-[9px] px-1 py-0.5 rounded bg-muted text-muted-foreground uppercase tracking-wide">
                              aspire
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          skipped
                            ? unskipPriorityWeek.mutate({ priorityId: p.id, weekStart })
                            : skipPriorityWeek.mutate({ priorityId: p.id, weekStart })
                        }
                        className={cn(
                          'flex-shrink-0 rounded p-2.5 md:p-0.5 -my-2 md:my-0 md:mt-0.5 transition-colors',
                          skipped
                            ? 'text-amber-600 hover:text-foreground'
                            : 'text-muted-foreground/40 hover:text-muted-foreground',
                        )}
                        aria-label={skipped ? `Unskip ${p.title} this week` : `Skip ${p.title} this week`}
                        title={skipped ? 'Restore this week' : 'Skip this week'}
                      >
                        {skipped ? <Eye size={13} /> : <EyeOff size={13} />}
                      </button>
                    </div>
                  )
                })}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

// ─── Week skip popover content ────────────────────────────────────────────────

function WeekSkipContent({
  weekStart,
  dateRange,
  cadencedPriorities,
  bucketMap,
}: {
  weekStart: string
  dateRange: string
  cadencedPriorities: Priority[]
  bucketMap: Map<string, Bucket>
}) {
  const skippedIds = useSkippedPriorityIds(weekStart)
  const skipPriorityWeek = useSkipPriorityWeek()
  const unskipPriorityWeek = useUnskipPriorityWeek()

  return (
    <div className="px-3 py-2.5 space-y-2">
      <p className="text-[11px] font-medium text-muted-foreground">{dateRange}</p>
      {cadencedPriorities.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">No cadenced priorities.</p>
      ) : (
        <div className="space-y-1">
          {cadencedPriorities.map((p) => {
            const bucket = p.bucketId ? bucketMap.get(p.bucketId) : null
            const color = p.color ?? bucket?.color ?? 'hsl(var(--muted-foreground))'
            const skipped = skippedIds?.has(p.id) ?? false
            return (
              <div key={p.id} className="flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                <span className={cn('text-xs flex-1 truncate', skipped && 'line-through text-muted-foreground')}>
                  {p.title}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    skipped
                      ? unskipPriorityWeek.mutate({ priorityId: p.id, weekStart })
                      : skipPriorityWeek.mutate({ priorityId: p.id, weekStart })
                  }
                  className={cn(
                    'flex-shrink-0 rounded p-2.5 md:p-0.5 -my-2 md:my-0 transition-colors',
                    skipped
                      ? 'text-amber-600 hover:text-foreground'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                  aria-label={skipped ? `Unskip ${p.title}` : `Skip ${p.title} this week`}
                  title={skipped ? 'Unskip' : 'Skip this week'}
                >
                  {skipped ? <Eye size={13} /> : <EyeOff size={13} />}
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Shared week bar ──────────────────────────────────────────────────────────

function WeekBar({
  week,
  height,
  width,
  bucketMap,
}: {
  week: WeekSlice
  height: number
  width?: number
  bucketMap: Map<string, Bucket>
}) {
  const monday = parseISO(week.weekStart)
  const sunday = addDays(monday, 6)
  const dateRange = `${format(monday, 'MMM d')} – ${format(sunday, 'MMM d')}`
  const sliceDesc = week.slices.length > 0
    ? week.slices.map(s => `${bucketMap.get(s.bucketId)?.name ?? 'Unknown'} ${Math.round(s.share * 100)}%`).join(', ')
    : null
  const ariaLabel = week.totalWeight > 0
    ? `Week of ${week.weekStart} · ${Math.round(week.totalWeight)} est. hrs${sliceDesc ? ` · ${sliceDesc}` : ''}`
    : `Week of ${week.weekStart} · no data`

  const bar = (
    <div
      className="rounded-sm overflow-hidden flex flex-col flex-shrink-0 cursor-default"
      style={{ height, width: width ?? '100%' }}
      role="img"
      aria-label={ariaLabel}
    >
      {week.totalWeight === 0 ? (
        <div className="h-full bg-muted/25" />
      ) : (
        week.slices.map((slice) => (
          <div
            key={slice.bucketId}
            style={{ height: `${slice.share * 100}%`, backgroundColor: slice.color }}
          />
        ))
      )}
    </div>
  )

  return (
    <Tooltip>
      <TooltipTrigger asChild>{bar}</TooltipTrigger>
      <TooltipContent side="top" className="p-0 overflow-hidden min-w-[140px]">
        <WeekTooltipContent week={week} bucketMap={bucketMap} dateRange={dateRange} />
      </TooltipContent>
    </Tooltip>
  )
}

function WeekTooltipContent({
  week,
  bucketMap,
  dateRange,
}: {
  week: WeekSlice
  bucketMap: Map<string, Bucket>
  dateRange: string
}) {
  return (
    <div className="px-3 py-2 space-y-1.5">
      <p className="text-[11px] font-medium text-muted-foreground">{dateRange}</p>
      {week.totalWeight === 0 ? (
        <p className="text-xs text-muted-foreground italic">No cadence data</p>
      ) : (
        <>
          <div className="space-y-1">
            {week.slices.map((slice) => {
              const bucket = bucketMap.get(slice.bucketId)
              if (!bucket) return null
              return (
                <div key={slice.bucketId} className="flex items-center gap-2">
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: slice.color }}
                  />
                  <span className="text-xs flex-1 truncate">{bucket.name}</span>
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {Math.round(slice.share * 100)}%
                  </span>
                </div>
              )
            })}
          </div>
          <p className="text-[10px] text-muted-foreground border-t pt-1.5">
            ~{Math.round(week.totalWeight)} est. hrs
          </p>
        </>
      )}
    </div>
  )
}

// ─── Bandwidth bar ────────────────────────────────────────────────────────────

function BandwidthBar({
  committed,
  status,
  label = 'Committed bandwidth',
}: {
  committed: number
  status: AllocationResult['bandwidthStatus']
  label?: string
}) {
  const MAX_SCALE = 80
  const pct = Math.min(100, (committed / MAX_SCALE) * 100)

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{label}</span>
        <span
          className={cn(
            'font-medium tabular-nums',
            status === 'red'   ? 'text-destructive'
            : status === 'amber' ? 'text-amber-600'
            : 'text-foreground',
          )}
        >
          ~{Math.round(committed)} h/wk
        </span>
      </div>
      <div className="relative h-2.5 bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full transition-all',
            status === 'red'   ? 'bg-destructive'
            : status === 'amber' ? 'bg-amber-500'
            : 'bg-emerald-500',
          )}
          style={{ width: `${pct}%` }}
        />
        {/* threshold markers */}
        <div className="absolute inset-y-0 w-px bg-amber-400/60"    style={{ left: `${(55 / MAX_SCALE) * 100}%` }} />
        <div className="absolute inset-y-0 w-px bg-destructive/60"  style={{ left: `${(65 / MAX_SCALE) * 100}%` }} />
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground select-none">
        <span>0</span>
        <span className="text-amber-600">55</span>
        <span className="text-destructive">65</span>
        <span>{MAX_SCALE}+ h/wk</span>
      </div>
    </div>
  )
}

// ─── Allocation panel ─────────────────────────────────────────────────────────

function AllocationPanel({
  byBucket,
  bucketMap,
  alignmentScore,
  totalWeight,
  period,
}: {
  byBucket: BucketAlloc[]
  bucketMap: Map<string, Bucket>
  alignmentScore: number | null
  totalWeight: number
  period: string
}) {
  // Sort by bucket rank (stated importance order) so the mismatch is immediately visible
  const sorted = [...byBucket]
    .filter((b) => bucketMap.has(b.bucketId))
    .sort((a, b) => {
      const ra = bucketMap.get(a.bucketId)!.rank
      const rb = bucketMap.get(b.bucketId)!.rank
      return ra - rb
    })

  // Gaps >= 3%: under-invested sorted by magnitude, over-invested after
  const gaps = [...byBucket]
    .filter((b) => b.gap !== null && Math.abs(b.gap!) >= 0.03)
    .sort((a, b) => b.gap! - a.gap!)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
          Allocation
        </p>
        <p className="text-xs text-muted-foreground">{period}</p>
      </div>

      {totalWeight === 0 ? (
        <p className="text-xs text-muted-foreground italic leading-relaxed">
          Add a cadence to your priorities to see allocation data here.
        </p>
      ) : (
        <>
          {/* Per-bucket bars — sorted by stated rank so mismatch is visible at a glance */}
          <div className="space-y-2.5">
            {sorted.map((b, rankIdx) => {
              const bucket = bucketMap.get(b.bucketId)
              if (!bucket) return null
              const pct = Math.round(b.share * 100)
              const targetPct = b.targetAlloc != null ? Math.round(b.targetAlloc * 100) : null
              const underInvested = b.gap != null && b.gap > 0.03

              return (
                <div key={b.bucketId}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-1.5 min-w-0">
                      {/* Rank badge (stated importance order) */}
                      <span className="text-[9px] text-muted-foreground/60 tabular-nums flex-shrink-0 w-3 text-right">
                        {rankIdx + 1}
                      </span>
                      {(() => {
                        const PanelIcon = getBucketIcon(bucket.icon)
                        return PanelIcon
                          ? <PanelIcon size={11} style={{ color: bucket.color }} aria-hidden className="flex-shrink-0" />
                          : <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: bucket.color }} />
                      })()}
                      <span className="text-xs truncate">{bucket.name}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                      <span className={cn(
                        'text-xs tabular-nums font-medium',
                        underInvested ? 'text-amber-600' : 'text-foreground',
                      )}>{pct}%</span>
                      {targetPct !== null && (
                        <span className="text-[10px] tabular-nums text-muted-foreground">
                          /{targetPct}%
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Bar: actual (filled) vs target (marker) */}
                  <div className="relative h-1.5 bg-muted rounded-full overflow-visible">
                    <div
                      className="absolute inset-y-0 left-0 rounded-full"
                      style={{ width: `${Math.min(100, pct)}%`, backgroundColor: bucket.color }}
                    />
                    {targetPct !== null && (
                      <div
                        className="absolute inset-y-0 w-0.5 -translate-x-px rounded-full bg-foreground/30"
                        style={{ left: `${Math.min(100, targetPct)}%` }}
                      />
                    )}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Gap summary (CL-2) */}
          {gaps.length > 0 && (
            <>
              <Separator />
              <div className="space-y-1.5">
                <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide">
                  Gaps
                </p>
                {gaps.map((b) => {
                  const bucket = bucketMap.get(b.bucketId)
                  if (!bucket) return null
                  const gapPct = Math.round(Math.abs(b.gap!) * 100)
                  const isUnder = b.gap! > 0
                  return (
                    <div key={b.bucketId} className="flex items-center justify-between text-xs">
                      <span className="truncate text-muted-foreground">{bucket.name}</span>
                      <span className={cn('tabular-nums font-medium flex-shrink-0 ml-2', isUnder ? 'text-amber-600' : 'text-blue-500')}>
                        {isUnder ? '−' : '+'}{gapPct}%
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          )}

          {/* Alignment score (CL-5) */}
          {alignmentScore !== null && (
            <>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Alignment score</span>
                <span
                  className={cn(
                    'text-sm font-semibold tabular-nums',
                    alignmentScore >= 70 ? 'text-emerald-600'
                    : alignmentScore >= 40 ? 'text-amber-600'
                    : 'text-destructive',
                  )}
                >
                  {alignmentScore}%
                </span>
              </div>
            </>
          )}
        </>
      )}
    </div>
  )
}
